import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight, Eye, Pencil, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { catLabel, fmtDate, useApi } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { halves } from "../components/home/FeatureSpread";
import { articleState, fmtDateTime, pubDate } from "./admin/status";
import { SITE, Seo, clip } from "../components/Seo";

const ArticleSeo = ({ a, blocks }) => {
  const description = clip(a.dek || blocks.find((b) => b.type === "p")?.text || a.title);
  const path = `/article/${a.slug}`;
  const image = a.cover?.startsWith("http") ? a.cover : `${SITE}${a.cover ?? "/img/hero-street.jpg"}`;
  const org = { "@type": "NewsMediaOrganization", name: "YOUTH", url: `${SITE}/`, logo: { "@type": "ImageObject", url: `${SITE}/apple-touch-icon.png` } };
  const by = a.author?.trim();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: clip(a.title, 110),
    description,
    image: [image],
    datePublished: a.published ? `${a.published}Z` : undefined,
    dateModified: a.updated_at || (a.published ? `${a.published}Z` : undefined),
    articleSection: a.category,
    keywords: Array.isArray(a.tags) && a.tags.length ? a.tags.join(", ") : undefined,
    author: by && !/^redazione/i.test(by) ? { "@type": "Person", name: by } : org,
    publisher: org,
    mainEntityOfPage: `${SITE}${path}`,
  };
  return <Seo title={`${clip(a.title, 70)} | YOUTH`} description={description} path={path} image={a.cover} type="article" jsonLd={jsonLd} />;
};

const pad = (n) => String(n).padStart(2, "0");
const Num = ({ n, className = "text-4xl" }) => (
  <span className={`wordmark text-pink leading-[0.75] pb-[0.15em] inline-block whitespace-nowrap ${className}`}>{pad(n)}</span>
);

const PreviewBar = ({ a }) => {
  const state = articleState(a);
  const label = state === "draft" ? "Draft — not visible on the site" : state === "scheduled" ? `Scheduled — goes live ${fmtDateTime(pubDate(a.published))}` : "Live on the site";
  return (
    <div className="flip fixed bottom-0 inset-x-0 z-[85] bg-ink text-cream border-t-4 border-cream" data-testid="preview-bar">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="kicker inline-flex items-center gap-2 text-hot"><Eye className="h-4 w-4" /> Preview</span>
        <span className="kicker text-cream/85" data-testid="preview-status">{label}</span>
        <div className="ml-auto flex gap-2">
          <Link to="/admin" className="kicker inline-flex items-center gap-2 border border-cream/40 px-3 py-2 hover:bg-cream hover:text-ink transition-colors" data-testid="preview-back-btn"><ArrowLeft className="h-4 w-4" /> Newsroom</Link>
          <Link to={`/admin/edit/${a.slug}`} className="kicker inline-flex items-center gap-2 bg-hot text-ink px-3 py-2 hover:bg-cream transition-colors" data-testid="preview-edit-btn"><Pencil className="h-4 w-4" /> Edit</Link>
        </div>
      </div>
    </div>
  );
};

const photoBy = (a) => String(a?.credits?.Photography ?? "").trim();

const Block = ({ b, first, by }) => {
  if (b.type === "h")
    return (
      <h2 id={`section-${b.n}`} className="scroll-mt-28 mt-16 mb-6 border-t-[3px] border-ink pt-4 grid grid-cols-[64px_1fr] sm:grid-cols-[80px_1fr] gap-3 items-start">
        <Num n={b.n} className="text-4xl sm:text-5xl" />
        <span className="display text-3xl sm:text-5xl">{b.text}</span>
      </h2>
    );
  if (b.type === "quote")
    return (
      <blockquote className="my-14 bg-paper border border-ink p-6 sm:p-10" data-testid="article-quote">
        <span className="wordmark block text-pink text-6xl sm:text-7xl leading-[0.8]" aria-hidden="true">y</span>
        <p className="font-serif italic text-3xl sm:text-4xl leading-[1.1] mt-6">“{b.text}”</p>
        {b.by && <cite className="kicker not-italic block mt-6">— {b.by}</cite>}
      </blockquote>
    );
  if (b.type === "image")
    return (
      <figure className="my-14 lg:-mr-24">
        <img src={b.src} alt={b.caption ?? ""} loading="lazy" className="w-full max-h-[85vh] object-cover" />
        <figcaption className="kicker text-[11px] mt-2.5 flex gap-3 text-ink/75">
          <span className="text-pink shrink-0">{pad(b.n)}</span>
          <span>{[b.caption, by && `Photography ${by}`].filter(Boolean).join(" · ")}</span>
        </figcaption>
      </figure>
    );
  return <p className={`text-lg sm:text-xl leading-relaxed text-ink/90 mb-6 ${first ? "dropcap" : ""}`}>{b.text}</p>;
};

const MetaStrip = ({ a }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 border-y-[3px] border-ink mt-10" data-testid="article-meta">
    {[["By", a.author], ["Published", fmtDate(a.published)], ["Read", `${a.read_time} min`]].map(([k, v], i) => (
      <div key={k} className={`py-4 pr-4 ${i ? "pl-4 border-l-2 border-ink" : ""} ${i === 2 ? "border-t-2 sm:border-t-0 border-l-0 sm:border-l-2 pl-0 sm:pl-4" : ""}`}>
        <p className="kicker text-rose">{k}</p>
        <p className="display text-xl sm:text-2xl mt-1">{v}</p>
      </div>
    ))}
    <button
      onClick={() => navigator.clipboard?.writeText(window.location.href).then(() => toast.success("Link copied"), () => toast.error("Could not copy the link"))}
      className="py-4 pl-4 border-l-2 border-t-2 sm:border-t-0 border-ink text-left hover:bg-hot transition-colors"
      data-testid="article-share-btn"
    >
      <p className="kicker text-rose">Share</p>
      <p className="display text-xl sm:text-2xl mt-1 inline-flex items-center gap-2"><Share2 className="h-5 w-5" /> Copy link</p>
    </button>
  </div>
);

// Like the printed opener: a type page (headline split top / bottom), then the photo full width.
const ArticleHero = ({ a }) => {
  const [top, bottom] = halves(a.title);
  const size = "text-[clamp(2.5rem,7.5vw,8rem)]";
  const by = photoBy(a);
  return (
    <header className="pt-28 sm:pt-36" data-testid="article-header">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <h1 className="sr-only">{a.title}</h1>
        <div className={`dz-type ${size}`} key={a.slug} data-testid="article-title" aria-hidden="true">
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>{top}</motion.p>
        </div>
        <div className="grid lg:grid-cols-12 gap-6 my-8 sm:my-12">
          <div className="lg:col-span-4 flex flex-col gap-4">
            <Link to={`/category/${a.category}`} className="kicker self-start hover:text-pink transition-colors" data-testid="article-category-link">{catLabel(a.category)}</Link>
            {a.dek && <p className="font-serif text-xl sm:text-2xl leading-snug">{a.dek}</p>}
            <p className="kicker text-ink/70">By {a.author}{by ? ` · Photography ${by}` : ""}</p>
          </div>
        </div>
        <div className={`dz-type ${size} text-right`} aria-hidden="true">
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}>{bottom}</motion.p>
        </div>
        <MetaStrip a={a} />
      </div>
      <figure className="mt-10">
        <img src={a.cover} alt={a.title} className="w-full max-h-[110vh] object-cover object-top" data-testid="article-cover" />
        <figcaption className="max-w-[1440px] mx-auto px-5 sm:px-10 kicker text-[11px] text-ink/75 mt-2.5">
          {a.title.split(":")[0]}{by ? ` · Photography ${by}` : ""}
        </figcaption>
      </figure>
    </header>
  );
};

const StoryIndex = ({ heads, a }) => (
  <aside className="lg:sticky lg:top-28 border-t-[3px] border-ink pt-4" data-testid="article-index">
    {heads.length > 0 && (
      <>
        <p className="kicker bg-ink text-cream inline-block px-2 py-1">In this story</p>
        <ol className="mt-3">
          {heads.map((h) => (
            <li key={h.n} className="border-b border-ink/20">
              <a
                href={`#section-${h.n}`}
                onClick={(e) => { e.preventDefault(); document.getElementById(`section-${h.n}`)?.scrollIntoView({ behavior: "smooth" }); }}
                className="group grid grid-cols-[48px_1fr] gap-2 py-3"
                data-testid={`article-index-${h.n}`}
              >
                <Num n={h.n} className="text-2xl" />
                <span className="display text-lg group-hover:text-rose transition-colors">{h.text}</span>
              </a>
            </li>
          ))}
        </ol>
      </>
    )}
    <p className="kicker text-ink/70 mt-5">{a.read_time} min read · {(a.gallery ?? []).length} photos</p>
  </aside>
);

const Credits = ({ credits }) => {
  const rows = Object.entries(credits ?? {});
  if (!rows.length) return null;
  return (
    <div className="mt-14 grid grid-cols-2 border-t-[3px] border-ink" data-testid="article-credits">
      {rows.map(([k, v], i) => (
        <div key={k} className={`py-4 border-b-2 border-ink/15 ${i % 2 ? "pl-4 border-l-2 border-l-ink" : "pr-4"}`}>
          <p className="kicker text-rose">{k}</p>
          <p className="font-semibold mt-1">{v}</p>
        </div>
      ))}
    </div>
  );
};

// Like the printed picture pages: big photos one after another, each with a small caption.
const Gallery = ({ images, a }) => {
  const [open, setOpen] = useState(null);
  const by = photoBy(a);
  return (
    <section className="py-16 sm:py-24 mt-20 border-t border-ink" data-testid="article-gallery">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="flex items-end justify-between gap-4 mb-10">
          <h2 className="dz-type text-5xl sm:text-7xl">In pictures</h2>
          <p className="kicker text-ink/70">{pad(images.length)} photos</p>
        </div>
        <div className="grid gap-12 sm:gap-20">
          {images.map((src, i) => (
            <Reveal key={src}>
              <figure className={`${i % 3 === 1 ? "lg:w-[62%] lg:ml-auto" : i % 3 === 2 ? "lg:w-[62%]" : ""}`}>
                <button onClick={() => setOpen(src)} className="block w-full" data-testid={`gallery-image-${i}`}>
                  <img src={src} alt={`${a.title} ${i + 1}`} loading="lazy" className="w-full h-auto max-h-[110vh] object-contain object-left" />
                </button>
                <figcaption className="kicker text-[11px] text-ink/75 mt-2.5 flex gap-3">
                  <span className="text-pink">{pad(i + 1)}</span>
                  <span>{a.title.split(":")[0]}{by ? ` · Photography ${by}` : ""}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-5xl bg-cream border-none p-2" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{a.title}</DialogTitle>
          {open && <img src={open} alt={a.title} className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const CELL = "p-5 sm:p-6 border-b-2 border-ink/15 sm:border-r-2 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(4n)]:border-r-0";

const Related = ({ items, cat }) =>
  items.length ? (
    <section className="max-w-[1440px] mx-auto px-5 sm:px-10 py-16 sm:py-24">
      <div className="flex items-end justify-between gap-4 pb-3">
        <h2 className="display text-5xl sm:text-7xl">More {catLabel(cat)}</h2>
        <Link to={`/category/${cat}`} className="kicker hover:text-rose" data-testid="related-view-all">All {cat} →</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-[3px] border-ink">
        {items.map((r, i) => (
          <Link key={r.slug} to={`/article/${r.slug}`} className={`group block ${CELL}`} data-testid={`related-card-${i}`}>
            <div className="img-zoom relative overflow-hidden aspect-[3/4] bg-blush">
              <img src={r.cover} alt={r.title} loading="lazy" className="h-full w-full object-cover" />
              <Num n={i + 1} className="absolute top-3 left-3 text-4xl drop-shadow-[0_2px_0_#1B1215]" />
            </div>
            <p className="kicker text-ink/70 mt-4">{fmtDate(r.published)} · {r.read_time} min</p>
            <h3 className="display text-2xl sm:text-3xl mt-2 group-hover:text-rose transition-colors">{r.title}</h3>
          </Link>
        ))}
      </div>
    </section>
  ) : null;

export default function Article({ preview = false }) {
  const { slug } = useParams();
  const { data, isError } = useApi(preview ? `/admin/preview/${slug}` : `/articles/${slug}`);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });
  const a = data?.article;

  if (isError) return <div className="min-h-screen grid place-items-center display text-5xl" data-testid="article-not-found"><Seo title="Story not found | YOUTH" noindex />Story not found.</div>;
  if (!a) return <div className="min-h-screen" />;

  let h = 0;
  let f = 0;
  const blocks = (a.body ?? []).map((b) => ({ ...b, n: b.type === "h" ? ++h : b.type === "image" ? ++f : null }));
  const heads = blocks.filter((b) => b.type === "h");
  const firstP = blocks.findIndex((b) => b.type === "p");

  return (
    <main data-testid="article-page" className={preview ? "pb-20" : ""}>
      {preview ? <Seo title={`Preview: ${a.title} | YOUTH`} noindex /> : <ArticleSeo a={a} blocks={blocks} />}
      {preview && <PreviewBar a={a} />}
      <motion.div style={{ scaleX: progress }} className="fixed top-0 left-0 right-0 h-1 bg-ink origin-left z-[80]" data-testid="reading-progress" />
      <ArticleHero a={a} />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 pt-14 grid lg:grid-cols-12 gap-10 lg:gap-12">
        <div className="lg:col-span-3"><StoryIndex heads={heads} a={a} /></div>
        <article className="lg:col-span-7 max-w-[720px]">
          {blocks.map((b, i) => <Block key={i} b={b} first={i === firstP} by={photoBy(a)} />)}
          <Credits credits={a.credits} />
          <div className="flex flex-wrap gap-2 mt-8">
            {(a.tags ?? []).map((t) => (
              <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="kicker border-2 border-ink px-3 py-1.5 hover:bg-hot transition-colors" data-testid={`article-tag-${t.replace(/\s+/g, "-")}`}>#{t}</Link>
            ))}
          </div>
        </article>
      </div>
      {a.gallery?.length > 0 && <Gallery images={a.gallery} a={a} />}
      {!preview && <Related items={data?.related ?? []} cat={a.category} />}
      {data?.next && (
        <Link to={`/article/${data.next.slug}`} className="group block bg-hot text-ink border-t-2 border-ink py-14 sm:py-20" data-testid="next-article-link">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <div className="flex items-center justify-between border-b-[3px] border-ink pb-3 mb-6">
              <p className="kicker">Next story</p>
              <ArrowRight className="h-8 w-8 group-hover:translate-x-3 transition-transform" />
            </div>
            <p className="display text-[clamp(2.25rem,5vw,5.5rem)] leading-[0.9] group-hover:text-cream transition-colors">{data.next.title}</p>
          </div>
        </Link>
      )}
    </main>
  );
}
